/**
 * TemplateSearch
 *
 * Controlled search bar with disabled state for basic users and loading
 * affordances.
 */
import React, { useState, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

const TemplateSearch = ({ 
  searchQuery, 
  setSearchQuery, 
  onSearch, 
  onClearSearch, 
  isLoading, 
  isBasicUser = false
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);

  // Sync inputValue with searchQuery when searchQuery changes from outside
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    if (isBasicUser) return;
    setInputValue(e.target.value);
  };

  const clearSearch = () => {
    if (isBasicUser) return;
    setInputValue('');
    if (onClearSearch) {
      onClearSearch();
    } else {
      setSearchQuery('');
      onSearch('');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isBasicUser) return;
    setSearchQuery(inputValue);
    onSearch(inputValue);
  };

  return (
    <div className="relative mb-6">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={`h-5 w-5 ${isBasicUser ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
          <input
            type="text"
            className={`block w-full pl-10 py-2.5 border border-gray-300 rounded-lg ${isBasicUser ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'} dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
            placeholder={isBasicUser ? "Search unavailable on Basic plan" : "Search templates..."}
            value={inputValue}
            onChange={handleSearchChange}
            disabled={isBasicUser}
          />
        </div>
        <button
          type="submit"
          disabled={!inputValue.trim() || isLoading || isBasicUser || inputValue.trim() === searchQuery.trim()}
          className={`px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors flex items-center gap-2 ${
            !inputValue.trim() || isBasicUser || inputValue.trim() === searchQuery.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
              : isLoading
                ? 'bg-primary-500 text-white cursor-wait'
                : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <span>Search</span>
          )}
        </button>
        
        {searchQuery && !isBasicUser && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-3 py-2.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center transition-colors"
            title="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </form>
    </div>
  );
};

export default TemplateSearch; 